import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Save, Mail, AlertCircle, Database, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface FormSettings {
  id: string;
  mappings: {
    [key: string]: string;
  };
  validation: {
    [key: string]: {
      required: boolean;
      pattern?: string;
      message?: string;
    };
  };
  submission: {
    successRedirect: string;
    errorMessage: string;
    sanitize: boolean;
    sendConfirmation: boolean;
  };
}

const defaultSettings: FormSettings = {
  id: '',
  mappings: {
    Form_FirstName: 'DB_FirstName',
    Form_LastName: 'DB_LastName',
    Form_Email: 'DB_EmailAddress',
    Form_Phone: 'DB_PhoneNumber'
  },
  validation: {
    Form_FirstName: {
      required: true,
      message: 'First name is required'
    },
    Form_LastName: {
      required: true,
      message: 'Last name is required'
    },
    Form_Email: {
      required: true,
      pattern: '^[A-Z0-9._%+-]+@[A-Z0-9.-]+\\.[A-Z]{2,}$',
      message: 'Please enter a valid email address'
    },
    Form_Phone: {
      required: true,
      pattern: '^[0-9]{10,}$',
      message: 'Please enter a valid phone number'
    }
  },
  submission: {
    successRedirect: '/thank-you',
    errorMessage: 'There was an error submitting the form. Please try again.',
    sanitize: true,
    sendConfirmation: true
  }
};

const LeadFormSettings: React.FC = () => {
  const [settings, setSettings] = useState<FormSettings>(defaultSettings);
  const queryClient = useQueryClient();

  // Save settings mutation
  const saveSettings = useMutation({
    mutationFn: async (formSettings: FormSettings) => {
      const { data, error } = await supabase
        .from('lead_forms')
        .update({ settings: formSettings })
        .eq('id', formSettings.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formSettings'] });
      toast.success('Form settings saved successfully');
    },
    onError: (error) => {
      toast.error('Failed to save settings');
      console.error('Settings save error:', error);
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveSettings.mutateAsync(settings);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Field Mappings */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Database className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-medium">Field Mappings</h3>
          </div>
          
          <div className="space-y-4">
            {Object.entries(settings.mappings).map(([formField, dbField]) => (
              <div key={formField} className="grid grid-cols-2 gap-4 items-center">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Form Field
                  </label>
                  <input
                    type="text"
                    value={formField}
                    disabled
                    className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Database Field
                  </label>
                  <input
                    type="text"
                    value={dbField}
                    onChange={(e) => setSettings({
                      ...settings,
                      mappings: {
                        ...settings.mappings,
                        [formField]: e.target.value
                      }
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Validation Rules */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-medium">Validation Rules</h3>
          </div>

          <div className="space-y-6">
            {Object.entries(settings.validation).map(([field, rules]) => (
              <div key={field} className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
                <h4 className="font-medium mb-4">{field.replace('Form_', '')}</h4>
                
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={rules.required}
                      onChange={(e) => setSettings({
                        ...settings,
                        validation: {
                          ...settings.validation,
                          [field]: {
                            ...rules,
                            required: e.target.checked
                          }
                        }
                      })}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-700">Required field</span>
                  </label>

                  {field === 'Form_Email' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email Pattern
                      </label>
                      <input
                        type="text"
                        value={rules.pattern}
                        onChange={(e) => setSettings({
                          ...settings,
                          validation: {
                            ...settings.validation,
                            [field]: {
                              ...rules,
                              pattern: e.target.value
                            }
                          }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                      />
                    </div>
                  )}

                  {field === 'Form_Phone' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Phone Pattern
                      </label>
                      <input
                        type="text"
                        value={rules.pattern}
                        onChange={(e) => setSettings({
                          ...settings,
                          validation: {
                            ...settings.validation,
                            [field]: {
                              ...rules,
                              pattern: e.target.value
                            }
                          }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Error Message
                    </label>
                    <input
                      type="text"
                      value={rules.message}
                      onChange={(e) => setSettings({
                        ...settings,
                        validation: {
                          ...settings.validation,
                          [field]: {
                            ...rules,
                            message: e.target.value
                          }
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submission Settings */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-medium">Submission Settings</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Success Redirect URL
              </label>
              <input
                type="text"
                value={settings.submission.successRedirect}
                onChange={(e) => setSettings({
                  ...settings,
                  submission: {
                    ...settings.submission,
                    successRedirect: e.target.value
                  }
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Error Message
              </label>
              <input
                type="text"
                value={settings.submission.errorMessage}
                onChange={(e) => setSettings({
                  ...settings,
                  submission: {
                    ...settings.submission,
                    errorMessage: e.target.value
                  }
                })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.submission.sanitize}
                  onChange={(e) => setSettings({
                    ...settings,
                    submission: {
                      ...settings.submission,
                      sanitize: e.target.checked
                    }
                  })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm text-gray-700">Enable data sanitization</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.submission.sendConfirmation}
                  onChange={(e) => setSettings({
                    ...settings,
                    submission: {
                      ...settings.submission,
                      sendConfirmation: e.target.checked
                    }
                  })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm text-gray-700">Send confirmation email</span>
              </label>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saveSettings.isLoading}
          >
            <Save className="h-4 w-4 mr-2" />
            {saveSettings.isLoading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadFormSettings;